import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, Maximize2, Plus, Sparkles, Trash2, X } from 'lucide-react';
import { useChat } from '../../contexts/ChatContext';
import Composer, { COMMANDS, ContextChips, ModelFooter } from './Composer';
import Thread from './Thread';

const mono = 'font-mono text-[10.5px] tracking-[0.12em]';

// Suggestions for the page the analyst is on. With a record selected, the
// first one is about that record.
export const suggestionsFor = (path, record) => {
  const r = record ? (record.label || record.id) : null;
  if (path.startsWith('/findings')) return [
    r ? `Is ${r} related to anything else on the network?` : 'Group today’s findings into likely incidents',
    'Which open findings are unassigned, and who should take them?',
    r ? `Have we seen anything like ${r} before?` : 'What needs attention first?',
    'Summarise this week for my weekly report',
  ];
  if (path.startsWith('/security-alerts')) return [
    r ? `Explain ${r} and whether it is a real threat` : 'Which open alerts are most urgent?',
    'Are any of these alerts on the same hosts as open findings?',
    'Which alerts look like false positives?',
    'Summarise the last 24 hours of alerts',
  ];
  if (path.startsWith('/assets/')) return [
    r ? `What changed on ${r} in the last 24 hours?` : 'What changed on this asset recently?',
    r ? `What is ${r} connected to, and is anything nearby affected?` : 'What is this asset connected to?',
    'Is its package drift expected?',
    '/timeline this asset for the last 7 days',
  ];
  if (path.startsWith('/cases/')) return [
    '/summarise this case',
    '/report this case for the IT director. One page, plain language.',
    'What is still open in this case?',
    'Are there findings that should be linked to this case?',
  ];
  if (path.startsWith('/playbooks')) return [
    'Which runs are waiting for approval, and what would approving allow?',
    r ? `Summarise ${r}` : 'Summarise the last run',
    'Which playbook fits the most urgent open finding?',
    'Why did the last failed run fail?',
  ];
  if (path.startsWith('/network-map')) return [
    'Which hosts have open findings or alerts, and are they next to each other?',
    'Which internet-facing assets have critical alerts?',
    'What is new on the network this week?',
    'Summarise risk by subnet',
  ];
  return [
    'What needs my attention today?',
    'Group today’s findings into likely incidents',
    'Which runs are waiting for approval?',
    'Summarise this week for my weekly report',
  ];
};

const ago = (iso) => {
  const d = iso ? new Date(String(iso).includes('T') ? iso : `${String(iso).replace(' ', 'T')}Z`) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  const m = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (m < 60) return m < 1 ? 'now' : `${m} min`;
  if (m < 1440) return `${Math.floor(m / 60)} h`;
  return `${Math.floor(m / 1440)} d`;
};

export const EmptyState = ({ onPick }) => {
  const { attached, conversations, loadConversation, config } = useChat();
  const location = useLocation();
  const record = attached.find(c => c.fromPage) || null;
  const sugg = suggestionsFor(location.pathname, record);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 pt-1.5">
        <h2 className="m-0 font-heading text-xl font-semibold tracking-[-0.01em]">What do you want to look into?</h2>
        <p className="m-0 text-[13px] leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
          I can read findings, alerts, assets, baselines, cases and playbook runs{config?.org ? ` in ${config.org}` : ''}, line events up across hosts, and draft write-ups. Anything that changes data asks you first.
        </p>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className={`${mono} pb-0.5`} style={{ color: 'var(--ink-faint)' }}>SUGGESTED FOR THIS PAGE</span>
        {sugg.map(t => (
          <button key={t} type="button" onClick={() => onPick(t)} className="min-h-11 box-border flex items-center gap-2.5 px-3 py-2 rounded-[10px] border text-left text-[13px] leading-snug hover:border-[color:var(--surf-line-strong)]" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
            <span className="flex-grow">{t}</span><ArrowRight className="w-3.5 h-3.5 shrink-0" strokeWidth={1.9} style={{ color: 'var(--ink-faint)' }} />
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-0.5">
        <span className={`${mono} pb-1.5`} style={{ color: 'var(--ink-faint)' }}>COMMANDS · TYPE / IN THE BOX</span>
        {COMMANDS.map(([k, d]) => (
          <span key={k} className="grid grid-cols-[96px_minmax(0,1fr)] gap-2.5 py-[5px] text-[12.5px]"><span className="font-mono text-xs" style={{ color: 'var(--ink-link)' }}>{k}</span><span style={{ color: 'var(--ink-muted)' }}>{d}</span></span>
        ))}
      </div>
      {conversations.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className={`${mono} pb-1`} style={{ color: 'var(--ink-faint)' }}>RECENT CHATS</span>
          {conversations.slice(0, 3).map(c => (
            <button key={c.id} type="button" onClick={() => loadConversation(c.id)} className="min-h-12 box-border flex flex-col justify-center gap-[3px] px-3 py-1.5 rounded-[10px] border text-left" style={{ borderColor: 'var(--surf-line)' }}>
              <span className="text-[13px] font-medium truncate">{c.title}</span>
              <span className="font-mono text-[10.5px]" style={{ color: 'var(--ink-faint)' }}>{ago(c.updated_at)}{c.case_title ? ` · saved to ${c.case_title}` : ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const History = ({ onDone }) => {
  const { conversations, loadConversation, deleteConversation, conversationId } = useChat();
  return (
    <div className="flex flex-col gap-1">
      <span className={`${mono} pb-1.5`} style={{ color: 'var(--ink-faint)' }}>YOUR CHATS · {conversations.length}</span>
      {!conversations.length && <span className="text-[13px]" style={{ color: 'var(--ink-faint)' }}>No saved chats yet.</span>}
      {conversations.map(c => (
        <div key={c.id} className="flex items-center gap-1 rounded-[10px] border" style={{ borderColor: c.id === conversationId ? 'var(--accent)' : 'var(--surf-line)' }}>
          <button type="button" onClick={() => { loadConversation(c.id); onDone(); }} className="flex-grow min-w-0 min-h-12 flex flex-col justify-center gap-[3px] px-3 py-1.5 text-left">
            <span className="text-[13px] font-medium truncate">{c.title}</span>
            <span className="font-mono text-[10.5px]" style={{ color: 'var(--ink-faint)' }}>{ago(c.updated_at)} · {c.message_count} message{c.message_count === 1 ? '' : 's'}{c.case_title ? ` · ${c.case_title}` : ''}</span>
          </button>
          <button type="button" onClick={() => deleteConversation(c.id)} aria-label={`Delete ${c.title}`} className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg mr-1" style={{ color: 'var(--ink-faint)' }}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </div>
  );
};

export default function AssistantPanel() {
  const { open, setOpen, messages, live, newChat, conversation, send } = useChat();
  const navigate = useNavigate();
  const location = useLocation();
  const [history, setHistory] = useState(false);
  if (!open || location.pathname.startsWith('/advisor')) return null;
  const empty = !messages.length && !live;
  const title = history ? 'Your chats' : conversation?.title || (empty ? 'New chat' : messages.find(m => m.role === 'user')?.content || 'Chat');
  const iconBtn = 'w-9 h-9 flex items-center justify-center rounded-lg border shrink-0';
  const iconStyle = { borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' };
  return (
    <aside id="assistant" aria-label="DragonEye assistant" className="fixed inset-0 top-16 z-40 lg:static lg:inset-auto lg:z-auto lg:w-[440px] lg:shrink-0 flex flex-col border-l min-h-0" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' }}>
      <span aria-hidden="true" className="absolute -left-px top-4 w-px h-6 hidden lg:block" style={{ background: 'var(--accent)' }} />
      <div className="h-14 shrink-0 box-border flex items-center gap-2.5 pl-[18px] pr-3 border-b" style={{ borderColor: 'var(--surf-line)' }}>
        <Sparkles className="w-[17px] h-[17px] shrink-0" strokeWidth={1.9} style={{ color: 'var(--accent)' }} />
        <span className="flex-grow min-w-0 flex flex-col gap-px">
          <span className="text-[14.5px] font-semibold">Assistant</span>
          <span className="font-mono text-[10.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{title}</span>
        </span>
        <button type="button" onClick={() => { newChat(); setHistory(false); }} aria-label="New chat" className={iconBtn} style={iconStyle}><Plus className="w-[15px] h-[15px]" strokeWidth={2} /></button>
        <button type="button" onClick={() => setHistory(v => !v)} aria-pressed={history} aria-label="Chat history" className={iconBtn} style={{ ...iconStyle, borderColor: history ? 'var(--accent)' : 'var(--surf-line)' }}><Clock className="w-[15px] h-[15px]" strokeWidth={1.9} /></button>
        <button type="button" onClick={() => navigate('/advisor')} aria-label="Open as full page" className={iconBtn} style={iconStyle}><Maximize2 className="w-[15px] h-[15px]" strokeWidth={1.9} /></button>
        <button type="button" onClick={() => setOpen(false)} aria-label="Close assistant" className={iconBtn} style={iconStyle}><X className="w-[13px] h-[13px]" strokeWidth={2} /></button>
      </div>
      {!history && (
        <div className="shrink-0 px-[18px] py-2.5 border-b" style={{ borderColor: 'var(--surf-line)' }}>
          <ContextChips />
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto box-border p-[18px]">
        {history ? <History onDone={() => setHistory(false)} /> : empty ? <EmptyState onPick={(t) => send(t)} /> : <Thread />}
      </div>
      {!history && (
        <div className="shrink-0 flex flex-col gap-2 px-[18px] pt-3 pb-3.5 border-t" style={{ borderColor: 'var(--surf-line)' }}>
          <Composer placeholder={empty ? 'Ask about findings, alerts, assets or cases. Type / for commands' : 'Ask a follow-up, or type / for commands'} />
          <ModelFooter />
        </div>
      )}
    </aside>
  );
}
